import { html, type TemplateResult } from '@design.estate/dees-element';
import type { IHarnessToolCall } from '../interfaces.js';
import type { IHarnessToolRenderHelpers } from '../toolregistry.js';
import {
  harnessFileWriteSource,
  harnessIsRecord,
  harnessLanguageForPath,
  harnessParseTodos,
  harnessStringifyValue,
  harnessStripAnsi,
  harnessSummarizeValue,
  harnessTerminalOutcome,
} from '../harness.helpers.js';
import '../dees-harness-todos/dees-harness-todos.js';

/**
 * Built-in tool-card bodies, one per registry kind. Kept as render functions
 * (not elements): they share the card chrome and differ only in body markup.
 * Custom tools override via IHarnessToolDescriptor.renderBody.
 */

const inputRecord = (call: IHarnessToolCall): Record<string, unknown> =>
  harnessIsRecord(call.input) ? call.input : {};

const outputText = (call: IHarnessToolCall): string => {
  if (call.errorText) return call.errorText;
  return harnessStringifyValue(call.output);
};

const filePathOf = (call: IHarnessToolCall): string => {
  const input = inputRecord(call);
  return String(input.path ?? input.filePath ?? input.file ?? '');
};

/** First file path named in an apply_patch envelope, for subtitles. */
export const applyPatchFirstPath = (call: IHarnessToolCall): string => {
  const patchText = inputRecord(call).patchText;
  if (typeof patchText !== 'string') return '';
  const match = patchText.match(/^\*\*\* (?:Add|Update|Delete) File: (.+)$/m);
  return match ? match[1].trim() : '';
};

const renderTerminal = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const command = String(input.command ?? '');
  // Shared with the collapsed-card summary so both read the same outcome.
  const { stdout, stderr, exitCode } = harnessTerminalOutcome(call);
  const running = call.status === 'pending' || call.status === 'running';
  return html`
    <div class="harness-terminal">
      ${command
        ? html`<div class="harness-terminal-command"><span class="prompt">$</span> ${command}</div>`
        : ''}
      ${stdout ? html`<pre class="harness-terminal-output">${harnessStripAnsi(stdout)}</pre>` : ''}
      ${stderr ? html`<pre class="harness-terminal-output tone-error">${harnessStripAnsi(stderr)}</pre>` : ''}
      ${call.errorText ? html`<pre class="harness-terminal-output tone-error">${harnessStripAnsi(call.errorText)}</pre>` : ''}
      ${running && !stdout && !stderr
        ? html`<div class="harness-terminal-running"><span class="cursor"></span> running…</div>`
        : ''}
      ${exitCode !== undefined
        ? html`<div class="harness-terminal-exit ${exitCode === 0 ? 'tone-success' : ''}">exit code ${exitCode}</div>`
        : ''}
    </div>
  `;
};

const renderFileRead = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const path = filePathOf(call);
  const content = outputText(call);
  if (!content) return helpers.keyValues([['file', path || '—']]);
  return helpers.codeBlock(content, harnessLanguageForPath(path), path.split('/').pop() || undefined);
};

const renderFileWrite = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  // The same reading of the input the row's diff stat counts, so the two can never disagree.
  const source = harnessFileWriteSource(call);
  if (source.kind === 'changes') {
    return html`${source.changes.map((change) => html`
      <section class="harness-file-change">
        ${helpers.keyValues([['file', change.path]])}
        ${helpers.diffBlock('', '', {
          language: harnessLanguageForPath(change.path),
          filename: change.path.split('/').pop(),
          unifiedDiff: change.diff,
        })}
      </section>
    `)}`;
  }
  if (source.kind === 'patch') {
    return html`${source.files.map((file) => {
      const patchFilename = file.path.split('/').pop() || undefined;
      const patchLanguage = harnessLanguageForPath(file.path);
      if (file.op === 'update') {
        return helpers.diffBlock(file.before, file.after, {
          language: patchLanguage,
          filename: patchFilename,
          unifiedDiff: file.unifiedDiff,
          showLineNumbers: file.lineNumbersKnown,
        });
      }
      if (file.op === 'add') {
        return helpers.codeBlock(file.after, patchLanguage, patchFilename);
      }
      return helpers.keyValues([['deleted', file.path]]);
    })}`;
  }
  const filename = source.path.split('/').pop() || undefined;
  const language = harnessLanguageForPath(source.path);
  if (source.before) {
    return helpers.diffBlock(source.before, source.after, {
      language,
      filename,
      showLineNumbers: !source.stringReplacement,
    });
  }
  if (source.after) {
    return helpers.codeBlock(source.after, language, filename);
  }
  return html`
    ${helpers.keyValues([['file', source.path || '—']])}
    ${outputText(call) ? helpers.monoBlock(outputText(call)) : ''}
  `;
};

const renderFileDelete = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  return html`
    ${helpers.keyValues([['file', filePathOf(call) || '—']])}
    ${outputText(call) ? helpers.monoBlock(outputText(call), call.status === 'error' ? { tone: 'error' } : undefined) : ''}
  `;
};

const renderDirList = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const output = call.output;
  const entries = Array.isArray(output)
    ? output.map((entry) => String(entry))
    : outputText(call).split('\n').filter(Boolean);
  return helpers.monoBlock(entries.join('\n'));
};

const renderBrowser = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const action = String(input.action ?? 'snapshot');
  const pairs: Array<[string, string]> = [['action', action]];
  if (input.url) pairs.push(['url', String(input.url)]);
  if (input.selector) pairs.push(['selector', String(input.selector)]);
  const output = call.output;
  let screenshotBase64 = '';
  if (harnessIsRecord(output)) {
    screenshotBase64 = String(output.screenshotBase64 ?? output.screenshot ?? output.dataBase64 ?? '');
  }
  const script = String(input.script ?? '');
  return html`
    ${helpers.keyValues(pairs)}
    ${script ? helpers.codeBlock(script, 'javascript') : ''}
    ${screenshotBase64 ? helpers.image(screenshotBase64, 'image/png', 'browser screenshot') : ''}
    ${!screenshotBase64 && outputText(call) ? helpers.monoBlock(outputText(call)) : ''}
  `;
};

const renderHttp = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const pairs: Array<[string, string]> = [];
  if (input.url) pairs.push(['url', String(input.url)]);
  const body = input.body !== undefined ? harnessStringifyValue(input.body) : '';
  const response = outputText(call);
  return html`
    ${pairs.length ? helpers.keyValues(pairs) : ''}
    ${body ? helpers.codeBlock(body, 'json') : ''}
    ${response ? helpers.codeBlock(response, 'json') : ''}
  `;
};

const renderJson = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const source = String(input.jsonString ?? '');
  return html`
    ${source ? helpers.codeBlock(source, 'json') : ''}
    ${outputText(call) ? helpers.monoBlock(outputText(call), call.isError || call.status === 'error' ? { tone: 'error' } : undefined) : ''}
  `;
};

const renderSearch = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const summary = harnessSummarizeValue(call.input);
  return html`
    ${summary ? helpers.monoBlock(summary) : ''}
    ${outputText(call) ? helpers.monoBlock(outputText(call)) : ''}
  `;
};

const renderSubtask = (call: IHarnessToolCall): TemplateResult => {
  const input = inputRecord(call);
  const prompt = String(input.prompt ?? input.description ?? '');
  const projectedResult = call.subtask?.previewState === 'ready'
    && call.subtask.status.type !== 'busy'
    ? [...call.subtask.messages].reverse().find(
        (message) => message.role === 'assistant' && Boolean(message.text),
      )?.text ?? ''
    : '';
  const result = outputText(call) || projectedResult;
  const terminal = call.subtask
    ? call.subtask.status.type !== 'busy'
    : call.status === 'completed' || call.status === 'error' || call.status === 'stopped';
  return html`
    ${prompt
      ? html`
          <section class="harness-subtask-detail harness-subtask-detail-brief">
            <div class="harness-subtask-detail-label">Agent Brief</div>
            <div class="harness-subtask-detail-text">${prompt}</div>
          </section>
        `
      : ''}
    ${result || terminal
      ? html`
          <section class="harness-subtask-detail harness-subtask-detail-result">
            <div class="harness-subtask-detail-label">Result</div>
            <div class="harness-subtask-detail-text">${result || 'No result was returned.'}</div>
          </section>
        `
      : ''}
  `;
};

const renderTodo = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const todos = harnessParseTodos(call.input);
  if (!todos.length) return helpers.monoBlock(harnessSummarizeValue(call.input) || outputText(call));
  return html`<dees-harness-todos compact .todos=${todos}></dees-harness-todos>`;
};

const projectTaskOutputItems = (output: unknown): unknown[] | undefined => {
  if (!harnessIsRecord(output)) return undefined;
  if (Array.isArray(output.tasks)) return output.tasks;
  if (Object.prototype.hasOwnProperty.call(output, 'task')) return [output.task];
  return harnessIsRecord(output.state) && Array.isArray(output.state.tasks)
    ? output.state.tasks
    : undefined;
};

const hasUnsupportedTodoStatus = (items: unknown[]): boolean => items.some((item) => {
  if (!harnessIsRecord(item) || typeof item.status !== 'string') return false;
  return item.status !== 'pending'
    && item.status !== 'in_progress'
    && item.status !== 'completed'
    && item.status !== 'cancelled';
});

const renderProjectTask = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const output = outputText(call);
  const outputItems = projectTaskOutputItems(call.output);
  if (outputItems !== undefined) {
    const tasks = hasUnsupportedTodoStatus(outputItems) ? [] : harnessParseTodos(outputItems);
    if (tasks.length) {
      return html`<dees-harness-todos compact .todos=${tasks}></dees-harness-todos>`;
    }
    return helpers.monoBlock(output || harnessStringifyValue(outputItems));
  }
  return helpers.monoBlock(output || harnessSummarizeValue(call.input));
};

const renderMcp = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  return html`
    ${call.contentBlocks?.length ? helpers.contentBlocks(call.contentBlocks) : ''}
    ${call.structuredContent !== undefined
      ? helpers.codeBlock(harnessStringifyValue(call.structuredContent), 'json')
      : ''}
    ${!call.contentBlocks?.length && call.structuredContent === undefined && outputText(call)
      ? helpers.monoBlock(outputText(call), call.isError ? { tone: 'error' } : undefined)
      : ''}
    ${call.input !== undefined && harnessSummarizeValue(call.input)
      ? helpers.monoBlock(harnessSummarizeValue(call.input))
      : ''}
  `;
};

const renderUnknown = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const inputSummary = harnessSummarizeValue(call.input);
  const inputJson = !inputSummary && call.input !== undefined ? harnessStringifyValue(call.input) : '';
  return html`
    ${inputSummary ? helpers.monoBlock(inputSummary) : ''}
    ${inputJson ? helpers.codeBlock(inputJson, 'json') : ''}
    ${outputText(call) ? helpers.monoBlock(outputText(call), call.status === 'error' ? { tone: 'error' } : undefined) : ''}
  `;
};

export const builtinBodyRenderers: Record<
  string,
  (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers) => TemplateResult
> = {
  'terminal': renderTerminal,
  'file-read': renderFileRead,
  'file-write': renderFileWrite,
  'file-delete': renderFileDelete,
  'dir-list': renderDirList,
  'browser': renderBrowser,
  'http': renderHttp,
  'json': renderJson,
  'search': renderSearch,
  'subtask': renderSubtask,
  'project-task': renderProjectTask,
  'todo': renderTodo,
  'mcp': renderMcp,
  'unknown': renderUnknown,
};
